import { Text } from '@chakra-ui/react'
import MfButton from '@components/branded/MfButton'
import { DateBox } from 'devextreme-react'
import moment from 'moment'
import React, { forwardRef, useEffect, useState, useCallback } from 'react'
import AceEditor from 'react-ace'
import DatePicker from 'react-datepicker'
import { Field } from 'react-final-form'
import ReactTooltip from 'react-tooltip'
import { Input, Label, FormGroup, Col, ButtonGroup, Row } from 'reactstrap'
import { titleizeField } from '../../utility/Utils'
import MfSelect from './MfSelect'
import MfSwitch from './MfSwitch'
import 'react-widgets/dist/css/react-widgets.css'

export function renderInput({
  input,
  label,
  meta = {},
  tooltipName,
  tooltipText,
  input_type = 'text',
  autocomplete,
  placeholder,
  helpText,
  maxLength,
  style,
  disabled = false
}) {
  const className = `form-group ${meta.error && meta.touched ? 'error' : ''}`

  return (
    <div className={className}>
      <div className="form-group">
        <div className="tooltip_label">
          <label> {label} </label>
          {renderTooltip(tooltipName, tooltipText)}
        </div>
        <input
          {...input}
          style={style}
          type={input_type}
          autoComplete={autocomplete ? autocomplete : 'off'}
          className="form-control"
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
        />
        {helpText && <small className="form-text text-muted">{helpText}</small>}
        {renderError(meta)}
      </div>
    </div>
  )
}

export function renderTooltip(name, text) {
  if (name && text) {
    return (
      <div className="d-inline-flex">
        <i data-tip data-for={name} className="fa-regular fa-circle-info icons font-m d-block text-dark"></i>
        <ReactTooltip data-multiline={true} id={name} place="right" type="dark" effect="solid">
          <p className="tooltip_text">{text}</p>
        </ReactTooltip>
      </div>
    )
  }
}

export function renderError({ error, touched }) {
  if (touched && error) {
    return (
      <div className="ui error message">
        <div className="header">{error}</div>
      </div>
    )
  }
}

export function renderCodeEditor({ input, label, meta, tooltipName, tooltipText, maxLength }) {
  return (
    <div>
      <div className="tooltip_label">
        <label> {label} </label>
        {renderTooltip(tooltipName, tooltipText)}
      </div>
      <div>
        <AceEditor
          placeholder="CSS code goes here"
          mode="css"
          name={input.name}
          fontSize={14}
          showPrintMargin={true}
          showGutter={true}
          highlightActiveLine={true}
          value={input.value}
          width="1100px"
          height="250px"
          setOptions={{
            enableBasicAutocompletion: false,
            enableLiveAutocompletion: false,
            enableSnippets: false,
            showLineNumbers: true,
            tabSize: 2
          }}
        />
        {renderError(meta)}
      </div>
    </div>
  )
}

export function renderNumber({
  input,
  label,
  meta,
  tooltipName,
  tooltipText,
  min = 0,
  step = 1,
  disabled = false,
  placeholder
}) {
  return (
    <div className="form-group">
      <div className="tooltip_label">
        <label>{label} </label>
        {renderTooltip(tooltipName, tooltipText)}
      </div>
      <div>
        <input
          {...input}
          min={min}
          step={step}
          type="number"
          className="form-control"
          disabled={disabled}
          placeholder={placeholder || `Select ${titleizeField(input.name)}`}
        />
        {renderError(meta)}
      </div>
    </div>
  )
}

export function renderTextarea({ input, label, meta, tooltipName, tooltipText, disabled = false }) {
  return (
    <div>
      <div className="tooltip_label">
        <label>{label} </label>
        {renderTooltip(tooltipName, tooltipText)}
      </div>

      <div>
        <textarea {...input} disabled={disabled} rows="10" cols="40" className="form-control" />
        {renderError(meta)}
      </div>
    </div>
  )
}

export function uploadFile({
  label,
  tooltipName,
  tooltipText,
  input: { value: omitValue, ...inputProps },
  meta: omitMeta,
  ...props
}) {
  return (
    <div>
      <div className={props.className || 'form-group mt-4'}>
        <div className="tooltip_label">
          <label> {label} </label>
          {renderTooltip(tooltipName, tooltipText)}
        </div>
        <input type="file" {...inputProps} {...props} />
      </div>
    </div>
  )
}

// Render dropdown using MfSelect (custom react-select component)
export function renderDropdownListNew({
  input,
  meta = {},
  className,
  label,
  tooltipName,
  tooltipText,
  placeholder,
  ...props
}) {
  return (
    <div className={className}>
      <div className="form-group">
        <div className="tooltip_label">
          <label> {label} </label>
          {renderTooltip(tooltipName, tooltipText)}
        </div>

        <MfSelect
          {...props}
          {...input}
          onChange={input.onChange}
          isDisabled={input.disabled}
          placeholder={placeholder || `Select ${titleizeField(input.name)}`}
        />
        {renderError(meta)}
      </div>
    </div>
  )
}

// the below component aims to fix the issue with using a controlled value in the multi-select
export function DropdownListNew2({
  input = {},
  meta = {},
  className,
  label,
  tooltipName,
  tooltipText,
  placeholder,
  getOptionValue,
  getOptionLabel,
  isMulti = false,
  ...rest
}) {
  // Handle value for single and multi-select using getOptionValue
  const selectedValue = isMulti
    ? rest.options?.filter(option => input.value?.includes(getOptionValue(option)))
    : rest.options?.find(option => getOptionValue(option) === input.value) || null

  const handleChange = selectedOption => {
    if (isMulti) {
      const value = selectedOption ? selectedOption.map(opt => getOptionValue(opt)) : []
      input.onChange(value) // Sync selected values with Redux Form
      if (value.length === 0 && rest.onClear) {
        rest.onClear() // Optional onClear callback
      }
    } else {
      const value = selectedOption ? getOptionValue(selectedOption) : null
      input.onChange(value) // Sync selected value with Redux Form
      if (!value && rest.onClear) {
        rest.onClear() // Optional onClear callback
      }
    }
  }
  return (
    <div className={className}>
      <div className="form-group">
        <div className="tooltip_label">
          <label>{label}</label>
          {renderTooltip(tooltipName, tooltipText)}
        </div>
        <MfSelect
          {...rest}
          value={selectedValue} // Use the correct selected value(s)
          onChange={handleChange} // Handle changes correctly using getOptionValue
          getOptionLabel={getOptionLabel} // Use custom label rendering
          getOptionValue={getOptionValue} // Use custom value handling
          isDisabled={input.disabled || rest.disabled}
          placeholder={placeholder || `Select ${titleizeField(input.name)}`}
          isMulti={isMulti}
        />
        {renderError(meta)}
      </div>
    </div>
  )
}

// the below component aims to fix the issue with using a controlled value in the multi-select
// export function DropdownListNew2({
//   input = {},
//   meta = {},
//   className,
//   label,
//   tooltipName,
//   tooltipText,
//   placeholder,
//   format,
//   parse,
//   ...props
// }) {
//   return (
//     <div className={className}>
//       <div className="form-group">
//         <div className="tooltip_label">
//           <label> {label} </label>
//           {renderTooltip(tooltipName, tooltipText)}
//         </div>
//         <MfSelect
//           {...props}
//           value={input.value} // added this
//           onChange={input.onChange}
//           isDisabled={input.disabled || props.disabled}
//           placeholder={placeholder || `Select ${titleizeField(input.name)}`}
//         />
//         {renderError(meta)}
//       </div>
//     </div>
//   )
// }

export const renderDatePicker = ({ input, label, meta, tooltipName, tooltipText, placeholder, ...rest }) => {
  return (
    <>
      <DatePicker
        placeholderText={placeholder}
        onChange={input.onChange}
        selected={input.value && new Date(input.value)}
        className="form-control"
        {...rest}
      />
      {renderError(meta)}
    </>
  )
}

export function renderDropdownList({
  input,
  data,
  valueField,
  textField,
  meta,
  label,
  text,
  tooltipName,
  tooltipText,
  defaultValue,
  emptyField,
  isTopMenu = false
}) {
  let className
  if (emptyField === undefined) {
    className = 'form-group'
  } else if (emptyField === false) {
    className = 'form-group error'
  } else {
    className = 'form-group'
  }
  return (
    <div className={className}>
      <label>{label}</label>
      {renderTooltip(tooltipName, tooltipText)}

      {defaultValue === undefined ? (
        <MfSelect
          {...input}
          options={data}
          textField={textField}
          onChange={value => input.onChange(value)}
          onBlur={() => input.onBlur(input.value)}
          label="* Stylesheet: "
          placeholder={text}
          getOptionValue={opt => opt[valueField]}
          getOptionLabel={opt => opt[textField]}
          menuPlacement={isTopMenu ? 'top' : 'auto'}
        />
      ) : (
        <MfSelect
          {...input}
          options={data}
          valueField={valueField}
          textField={textField}
          onChange={value => input.onChange(value)}
          onBlur={() => input.onBlur(input.value)}
          label="* Stylesheet: "
          placeholder={text}
          value={input.value.id ? input.value.id : defaultValue}
          getOptionValue={opt => opt[valueField]}
          getOptionLabel={opt => opt[textField]}
          menuPlacement={isTopMenu ? 'top' : 'auto'}
        />
      )}
      {renderError(meta)}
    </div>
  )
}

export function renderCheckBox({ input, label, tooltipName, tooltipText, meta, disabled = false }) {
  return (
    <div className="form-group">
      <input {...input} type="checkbox" checked={input.value} disabled={disabled} />
      &nbsp;
      <div className="tooltip_checkbox">
        <span>{label}</span>
        {renderTooltip(tooltipName, tooltipText)}
      </div>
      {renderError(meta)}
    </div>
  )
}

export const ReduxFormSwitch = ({ input, disabled, label, tooltipName, tooltipText }) => {
  return (
    <MfSwitch
      {...input}
      disabled={disabled}
      label={label}
      tooltipType="icon"
      tooltip={tooltipText}
      checked={input.value}
      onChange={input.onChange}
    />
  )
}

export function renderTzSelect({ input, label, options, selectedTimezone }) {
  const timezone = options.find(option => option.value === selectedTimezone)
  return (
    <div className="form-group">
      <label>{label}</label>
      <MfSelect
        {...input}
        options={options}
        onChange={input.onChange}
        onBlur={() => input.onBlur(input.value)}
        value={timezone ? timezone : ''}
        getOptionValue={opt => opt.value}
        getOptionLabel={opt => opt.label}
      />
    </div>
  )
}

export function renderSkuSelect({ input, label, options, sku }) {
  const ListItem = ({ item }) => <span>{`${item.sku} | ${item.name}`}</span>

  return (
    <div className="form-group">
      <label>{label}</label>
      <MfSelect
        options={options}
        onChange={value => input.onChange(value)}
        itemComponent={ListItem}
        textField={item => (item ? `${item.sku} | ${item.name}` : sku)}
        valueField="sku"
        defaultValue={options.find(element => element.sku === sku)}
        getOptionLabel={opt => opt.name}
        getOptionValue={opt => opt.sku}
      />
    </div>
  )
}

// only used in sub order address form
const TextInputFieldComponent = (
  { label, fieldName, labelWidth = 4, inputWidth = 8, value, handleChange, error = '', ...inputProps },
  ref
) => {
  if (!label && fieldName) label = titleizeField(fieldName)
  return (
    <FormGroup row className="mb-0">
      <Label sm={labelWidth} className="address-format-label pr-0" for={fieldName}>
        {label}
      </Label>
      <Col sm={inputWidth} style={{ alignSelf: 'center' }}>
        <Input
          innerRef={ref}
          className="w-100"
          style={{ width: '100%' }}
          type="text"
          name={fieldName}
          id={fieldName}
          onChange={handleChange}
          value={value}
          {...inputProps}
        />
        {error && (
          <div className="ui error message">
            <div className="header">{error}</div>
          </div>
        )}
      </Col>
    </FormGroup>
  )
}

export const TextInputField = forwardRef(TextInputFieldComponent)

// export const renderColorPicker = ({ input, label, meta, tooltipName, tooltipText }) => {
//   return (
//     <div>
//       <div className="tooltip_label">
//         <label> {label} </label>
//         {renderTooltip(tooltipName, tooltipText)}
//       </div>
//       <input {...input} type="color" className="form-control" />
//       {renderError(meta)}
//     </div>
//   )
// }

export const MfDatePicker = DateBox

export function renderDateRangeBox({ input, meta, label, tooltipName, tooltipText, ...rest }) {
  const { value: inputValue, onChange: inputOnChange } = input
  const [startDate, setStartDate] = useState(inputValue ? inputValue[0] : null)
  const [endDate, setEndDate] = useState(inputValue ? inputValue[1] : null)
  const [selectedDays, setSelectedDays] = useState(0)
  const [isIndefinite, setIsIndefinite] = useState(false)

  const convertRangeToDays = useCallback(([startDate, endDate]) => {
    if (!endDate) return 'Indefinite'
    const start = moment(startDate)
    const end = moment(endDate)
    return end.diff(start, 'days') + 1
  }, [])

  const handleStartDateChange = useCallback(({ value }) => setStartDate(value), [])

  const handleEndDateChange = useCallback(({ value }) => setEndDate(value), [])

  useEffect(() => {
    inputOnChange([startDate, endDate])
  }, [inputOnChange, startDate, endDate])

  useEffect(() => {
    const startDate = inputValue[0]
    const endDate = inputValue[1]
    if (startDate && endDate) {
      setSelectedDays(convertRangeToDays([startDate, endDate]))
      setIsIndefinite(false)
    } else if (startDate && !endDate) {
      setSelectedDays('Indefinite')
      setIsIndefinite(true)
    }

    // Cleanup
    return () => {
      setSelectedDays(0)
      setIsIndefinite(false)
    }
  }, [convertRangeToDays, inputValue])

  return (
    <div>
      <div className="tooltip_label">
        <label>{label}</label>
        {renderTooltip(tooltipName, tooltipText)}
      </div>
      <Row>
        <Col sm={4}>
          <MfDatePicker
            type="datetime"
            value={inputValue[0]}
            onValueChanged={handleStartDateChange}
            showClearButton
            applyValueMode="useButtons"
            aria-label="Select start date"
            label="Start Date"
            {...rest}
          />
        </Col>
        <Col sm={1} className="d-flex align-items-center justify-content-center duration">
          <i className="fa-solid fa-arrow-right"></i>
        </Col>
        <Col sm={4}>
          <MfDatePicker
            type="datetime"
            value={inputValue[1]}
            onValueChanged={handleEndDateChange}
            showClearButton
            applyValueMode="useButtons"
            aria-label="Select end date"
            label="End Date"
            {...rest}
          />
        </Col>
        <Col sm={3} className="d-flex align-items-center duration">
          <span>Selected Duration: </span>
          <span aria-live="polite">{isIndefinite ? 'Indefinite' : `${selectedDays} day(s)`}</span>
        </Col>
      </Row>
      {renderError(meta)}
    </div>
  )
}

export function renderButtonGroup({
  input: { value, onChange },
  meta,
  label,
  buttonOptions,
  tooltipName,
  tooltipText,
  disabled = false,
  ...rest
}) {
  const [selectedIndex, setSelectedIndex] = useState(value || null) // Simplify initial state

  const handleButtonClick = index => {
    setSelectedIndex(index)
    onChange(index)
  }

  useEffect(() => {
    setSelectedIndex(value)
  }, [value])

  return (
    <>
      <div className="tooltip_label">
        <label>{label}</label>
        {renderTooltip(tooltipName, tooltipText)}
      </div>
      <ButtonGroup>
        {buttonOptions?.map((buttonData, index) => {
          return (
            <React.Fragment key={index}>
              <MfButton
                key={index}
                data-tip
                data-for={`${buttonData.name}${index}`}
                className={selectedIndex === index ? 'button-group-selected' : 'button-group-unselected'}
                onClick={() => handleButtonClick(index)}
                disabled={disabled}
              >
                {buttonData.name}
              </MfButton>
              {buttonData.toolTipText && (
                <ReactTooltip key={index} id={`${buttonData.name}${index}`} place="top" effect="solid">
                  {buttonData.toolTipText}
                </ReactTooltip>
              )}
            </React.Fragment>
          )
        })}
      </ButtonGroup>
      {renderError(meta)}
    </>
  )
}

export function renderPriceInput({
  input,
  label,
  meta = {},
  tooltipName,
  tooltipText,
  placeholder = 'Enter Price',
  disabled = false,
  style,
  isDollarAmount,
  parse,
  allowNegative = false
}) {
  const className = `form-group ${meta.error && meta.touched ? 'error' : ''}`
  const [displayValue, setDisplayValue] = useState('')

  useEffect(() => {
    const numericValue =
      input.value !== undefined && input.value !== null && !isNaN(input.value) ? parseFloat(input.value) : NaN

    if (!isNaN(numericValue)) {
      const valueToDisplay = isDollarAmount ? numericValue : numericValue / 100
      setDisplayValue(valueToDisplay.toFixed(2))
    } else {
      setDisplayValue('')
    }
  }, [input.value, isDollarAmount])

  const handleChange = e => {
    const value = e.target.value
    if (
      value === '' ||
      /^\d*\.?\d{0,2}$/.test(value) ||
      (allowNegative && (value === '-' || /^-?\d*\.?\d{0,2}$/.test(value)))
    ) {
      setDisplayValue(value)
    }
  }

  const parseValue = numericValue => {
    if (parse) {
      return parse(numericValue)
    }
    return Math.round(numericValue * 100)
  }

  // Convert the value to cents when the user leaves the input
  const handleBlur = () => {
    const numericValue = parseFloat(displayValue)

    if (!isNaN(numericValue)) {
      input.onChange(parseValue(numericValue))
    } else {
      input.onChange('0.00')
    }

    setDisplayValue(numericValue ? numericValue.toFixed(2) : '0.00')
  }

  return (
    <div className={className}>
      <div className="form-group">
        <div className="tooltip_label">
          <label>{label}</label>
          {renderTooltip(tooltipName, tooltipText)}
        </div>
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="form-control"
          disabled={disabled}
          style={style}
        />
        {renderError(meta)}
      </div>
    </div>
  )
}

export const FormInputWithParentSwitch = ({
  name,
  component,
  label,
  tooltipName,
  tooltipText,
  parentName = null,
  fieldIsSwitch = false,
  parentSwitchEnabled = false,
  isFormDisabled = false,
  fieldColWidth,
  switchLabel = 'Use Parent Value',
  switchOnChange = null,
  tooltipType = 'icon',
  displayLabel = false,
  labelPosition = 'right',
  disabled = false,
  displayParentSwitch = false,
  customButton = null,
  unit = null,
  ...rest
}) => (
  <div id={name}>
    <Row className="row-with-switch">
      <Col className="switch-container" xs={fieldColWidth}>
        {fieldIsSwitch ? (
          <Field name={parentSwitchEnabled ? `parentValues.${name}` : name} type="checkbox" {...rest}>
            {({ input }) => (
              <MfSwitch
                tooltipName={tooltipName}
                tooltip={tooltipText}
                label={label}
                checked={input.checked}
                onChange={input.onChange}
                disabled={isFormDisabled || parentSwitchEnabled || disabled}
                tooltipType={tooltipType}
                labelPosition={labelPosition}
              />
            )}
          </Field>
        ) : (
          <>
            <div className="tooltip_label">
              <label>
                {label}
                {unit && (
                  <Text as="span" color="neutral.500">
                    {` (${unit})`}
                  </Text>
                )}
              </label>
              {renderTooltip(tooltipName, tooltipText)}
            </div>
            <Field
              name={parentSwitchEnabled ? `parentValues.${name}` : name}
              component={component}
              label={displayLabel && label}
              disabled={isFormDisabled || parentSwitchEnabled || disabled}
              unit={unit}
              {...rest}
            />
          </>
        )}
      </Col>
      {customButton && (
        <Col xs="auto" className="d-flex align-items-center">
          {customButton}
        </Col>
      )}
      {displayParentSwitch && (
        <Col
          xs={fieldColWidth ? 12 - fieldColWidth : 6}
          sm={fieldColWidth ? 12 - fieldColWidth : 6}
          md={fieldColWidth ? 12 - fieldColWidth : 5}
          lg={fieldColWidth ? 12 - fieldColWidth : 4}
          xl={fieldColWidth ? 12 - fieldColWidth : 3}
          className="d-flex justify-content-end align-items-center"
        >
          <Field name={parentName ? parentName : `use_parent_${name}`} type="checkbox">
            {({ input }) => (
              <MfSwitch
                tooltipName={tooltipName}
                tooltipText={tooltipText}
                label={switchLabel}
                checked={input.checked}
                onChange={e => {
                  input.onChange(e)
                  if (switchOnChange) {
                    switchOnChange()
                  }
                }}
                disabled={isFormDisabled}
              />
            )}
          </Field>
        </Col>
      )}
    </Row>
    <hr />
  </div>
)
